"use client";

import { playNotificationSound } from "@/lib/notification-sounds";
import { isEventEnabled, readSoundPreferences } from "@/lib/notification-preferences";
import { ehDeOutro } from "./autoria-mensagem";

/**
 * #411 — som de mensagem nova no Chat Interno.
 *
 * O Chat Interno nunca tocou som. O mecanismo já existia inteiro (evento, beep,
 * preferência por evento em `/dashboard/perfil`) e `mensagemNova` até tinha som
 * configurado — mas **nenhum código disparava**, só os testes.
 *
 * Chave própria (`mensagemInterna`) em vez de reusar `mensagemNova`: são
 * contextos diferentes e o atendente pode querer o som do atendimento sem o do
 * chat da equipe. O timbre é o mesmo de propósito — inventar um terceiro só
 * aumentaria o que se precisa decorar.
 */

/**
 * Throttle de 2s, o mesmo do `useNotificationSound` do chat headless.
 *
 * Sem ele, uma rajada (colega mandando cinco linhas seguidas) vira cinco beeps
 * — que é como uma feature de notificação vira a primeira coisa que o usuário
 * desliga.
 */
const THROTTLE_MS = 2_000;
let ultimoToqueEm = 0;

/**
 * #466: canal que a tela do Chat Interno está mostrando agora (null = a tela não
 * está aberta). O som saiu do `ChatInternoLayout` pro `InternalUnreadProvider`,
 * que vive no layout do dashboard e escuta o socket em qualquer tela. O provider
 * não sabe qual canal está na tela; a página avisa por aqui.
 */
let canalAbertoId: string | null = null;

export function marcarCanalAberto(channelId: string | null): void {
  canalAbertoId = channelId;
}

export function estaNoCanalAberto(channelId: string): boolean {
  return canalAbertoId !== null && canalAbertoId === channelId;
}

export interface ContextoMensagem {
  /** Autor da mensagem. */
  senderId: string | null | undefined;
  /** Quem está logado nesta aba. */
  currentUserId: string | null | undefined;
  /** A mensagem chegou no canal que está aberto na tela? */
  noCanalAberto: boolean;
  /** `document.visibilityState === "visible"` no momento do evento. */
  janelaVisivel: boolean;
  /** Aviso automatico: assinado por um membro real, mas nao escrito por ele. */
  isSystem?: boolean;
}

/**
 * Decide se o beep deve tocar. Pura de propósito — o `ChatInternoLayout` é um
 * componente grande, e o que pode dar errado aqui é a REGRA, não a fiação.
 *
 * | situação | toca? |
 * |---|---|
 * | minha própria mensagem | não — inclusive de outra aba |
 * | canal fechado | **sim** |
 * | canal aberto, janela escondida | **sim** — não estou vendo |
 * | canal aberto, janela visível | não — estou lendo |
 *
 * A última linha é o que separa "notificação" de "ruído": beep a cada linha de
 * uma conversa que você está lendo na tela não informa nada.
 */
export function deveTocar(ctx: ContextoMensagem): boolean {
  // Autoria vem de `ehDeOutro`, nao reimplementada aqui: a regra ja nasceu
  // duplicada em quatro pontos uma vez e o conserto saiu pela metade duas
  // vezes seguidas. Aqui fica so o que e do som — canal aberto e visibilidade.
  if (!ehDeOutro({ senderId: ctx.senderId, isSystem: ctx.isSystem }, ctx.currentUserId)) {
    return false;
  }
  if (!ctx.noCanalAberto) return true;
  return !ctx.janelaVisivel;
}

/**
 * Toca se a regra permitir, respeitando a preferência do usuário
 * (`mensagemInterna`) e o throttle — nessa ordem, para o relógio não ser
 * consumido por quem está com o som desligado.
 *
 * `agora` é injetável para o teste não depender de relógio real.
 */
export function tocarSeDevido(ctx: ContextoMensagem, agora = Date.now()): boolean {
  if (!deveTocar(ctx)) return false;

  // A preferência é checada ANTES do throttle, e o throttle só é consumido se
  // o som de fato saiu. Fazer o contrário (delegar a checagem pro
  // `playNotificationSoundIfEnabled`, depois de marcar o relógio) deixava o
  // throttle rodando pra quem tem o som DESLIGADO — inofensivo hoje, mas é a
  // função devolvendo `true` para "toquei" quando não tocou nada.
  const prefs = readSoundPreferences();
  if (!isEventEnabled(prefs, "mensagemInterna")) return false;
  // "Só tocar com a aba escondida" (/dashboard/perfil), como o
  // `useNotificationSound` do atendimento já respeita. Pesava pouco enquanto o
  // som só tocava dentro do Chat Interno; tocando em qualquer tela (#466), quem
  // ligou a opção ouviria beep com a plataforma na frente.
  if (prefs.onlyWhenHidden && ctx.janelaVisivel) return false;

  if (agora - ultimoToqueEm < THROTTLE_MS) return false;
  ultimoToqueEm = agora;
  playNotificationSound("mensagemInterna");
  return true;
}

/** Só para teste — zera o throttle entre casos. */
export function _resetThrottle(): void {
  ultimoToqueEm = 0;
}
